/**
 * Bundle 80.2 Task 2 - Testimonial card.
 * Warm quote card. No portrait, no personal data beyond the
 * participant's chosen attribution display (initials or first
 * name). Anonymous attribution was retired in Bundle 156a.
 * Attribution line format ("Identifier, age, gender, event") is
 * assembled by frontend/src/utils/testimonialDisplay.js so every
 * public surface renders identically.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatTestimonialParts } from '@/utils/testimonialDisplay';
import { isPastEvent } from '@/utils/eventCountdown';

function formatEventDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: 'short', year: 'numeric',
    });
  } catch (_) {
    return null;
  }
}

const TestimonialCard = ({ t, compact = false }) => {
  const dateLabel = formatEventDate(t?.event_date);
  const { identity, event_title } = formatTestimonialParts(t);
  // Bundle 156b - soft-link the event title. Past events go to
  // /events/{slug}?tab=past so users land on the correct tab of
  // the Bundle 155 archive; upcoming events go to the detail
  // page directly.
  const slug = t?.event_slug;
  const past = isPastEvent(t?.event
    || { end_datetime: t?.event_end_datetime,
          start_datetime: t?.event_date });
  const eventHref = slug
    ? (past ? `/events/${slug}?tab=past` : `/events/${slug}`)
    : null;
  return (
    <Card className="h-full" data-testid={`testimonial-card-${t.id}`}>
      <CardContent className={`p-5 h-full flex flex-col ${compact ? 'gap-2' : 'gap-3'}`}>
        <div className="text-secondary-600 font-display text-2xl leading-none">&ldquo;</div>
        <blockquote className="text-primary-900 italic leading-relaxed text-sm sm:text-base flex-1"
                       data-testid={`testimonial-quote-${t.id}`}>
          {t.quote_text_final}
        </blockquote>
        <div className="pt-2 border-t border-neutral-100 flex flex-wrap items-center gap-2 justify-between">
          <div
            className="text-primary-800 text-xs font-medium tracking-wide"
            data-testid={`testimonial-attribution-${t.id}`}
          >
            {identity && event_title ? (
              <>
                <span>{identity}</span>
                <span className="mx-2 text-neutral-400">&#183;</span>
                {eventHref ? (
                  <Link
                    to={eventHref}
                    className="underline decoration-transparent hover:decoration-secondary-500 transition-colors"
                    data-testid={`testimonial-event-link-${t.id}`}
                  >
                    {event_title}
                  </Link>
                ) : (
                  <span>{event_title}</span>
                )}
              </>
            ) : identity ? (
              <span>{identity}</span>
            ) : event_title ? (
              eventHref ? (
                <Link
                  to={eventHref}
                  className="underline decoration-transparent hover:decoration-secondary-500 transition-colors"
                  data-testid={`testimonial-event-link-${t.id}`}
                >
                  {event_title}
                </Link>
              ) : <span>{event_title}</span>
            ) : null}
          </div>
          <div className="flex items-center gap-2 text-[11px] text-neutral-500">
            {dateLabel ? <span>{dateLabel}</span> : null}
            {t.event_format ? (
              <Badge variant="outline" className="text-[10px]">{t.event_format}</Badge>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TestimonialCard;
